import React, {ReactNode, useContext} from 'react';
import './Editable.css';
import 'react-phone-number-input/style.css'
import {EditableContext} from "./EditableContext";
import {findError} from "../../util/ErrorUtil";

/**
 * <E> is the enclosing entity type
 * <V> is the type of the value field being edited
 */
export type EditableProps<EnclosingType extends object, V extends any> = {
  
  // The field in the object that's being edited. This can be used in place
  // of a dedicated getter and setter.
  field?: keyof EnclosingType,

  // These are less preferred than using the field, but sometimes the user needs
  // to do more than blindly set the field to the value.
  getter?: (entity: EnclosingType) => V | null | undefined,
  setter?: (entity: EnclosingType, value: V) => EnclosingType,

  // Correlates this field with errors from the rpc response. If not set,
  // the field name is used.
  errorKey?: keyof EnclosingType | string,

  label?: string | null | undefined | ((workingCopy: EnclosingType) => string | null | undefined),
  helpText?: string | null | undefined | ((isEditing: boolean, workingCopy: EnclosingType) => string | null | undefined);
  visibleWhen?: (entity: EnclosingType) => boolean | null | undefined;
  
  // True if the value can only be set in create mode (not updated)
  immutable?: boolean,
}

type Props<EnclosingType extends object, V extends any> = EditableProps<EnclosingType, V> & {
  children: ReactNode
}

/**
 * This just provides the boilerplate
 */
export const Editable = (props: Props<any, any>) => {

  const {editing, workingCopy, errors} = useContext(EditableContext)
  const error = findError(errors, (props.errorKey ?? props.field ?? '') as string)

  if (props.visibleWhen && !props.visibleWhen(workingCopy)) {
    return <></>
  }
  
  return <div className={'Editable'}>
    {props.label && <div className={'EditableLabel'}>
      {typeof props.label == 'function' ? props.label(workingCopy) :props.label}
    </div>}

    {props.children}
    
    {props.helpText && <div className={'EditableHelpText'}>
      {typeof props.helpText == 'function' ? props.helpText(editing, workingCopy) : props.helpText}
    </div>}
    {error && <div className={'EditableError'}>{error}</div>}
  </div>

}